import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, AUTH_TOKEN } from '../config';

// PremiereMoment — the payoff (Build_Game_Intro §4.6).
//
// They walk out. The DJ premieres the track on air — heard on the street.
// Radio doesn't show artwork, so nothing needs approving mid-scene and the
// emotional beat runs uninterrupted; the first play precedes the drop, which
// is how records actually work.
//
// Armed by the session (release consent → store.premiere), fired by the door:
// the moment the player steps from the studio interior onto the street, their
// record takes the city's audio — audioEngine.playTrack stops the station,
// plays it LOUD, and announces it on the now-playing ticker, so the world's
// audio-reactive layer responds to THEIR song. An ON AIR card carries the
// premiere framing; the walk-out also reports the drop quest's goto objective,
// because stepping outside IS Act 4's objective.
//
// Diegetic music rule: the song does not have to match the mood of the street.
// Radio plays what radio plays — that's the point.

export default function PremiereMoment() {
  const premiere = useWorldStore((s) => s.premiere);
  const interior = useWorldStore((s) => s.interior);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const [card, setCard] = useState(null);
  const firedRef = useRef(null);   // trackId already premiered — once, ever

  useEffect(() => {
    if (!premiere || interior) return undefined;           // armed, still inside
    if (firedRef.current === premiere.trackId) return undefined;
    firedRef.current = premiere.trackId;

    // ── ON AIR ──────────────────────────────────────────────────────────────
    if (premiere.audioUrl) {
      audioEngine.playTrack({
        track_id: premiere.trackId,
        title: premiere.title || 'Your record',
        audio_url: premiere.audioUrl,
        avatar_name: 'World premiere',
      });
    }
    setCard({
      title: premiere.title || 'Your record',
      station: premiere.stations?.[0]?.name || 'HypeTown Radio',
    });

    // Stepping outside is Act 4's objective — report it, then sync so the
    // reward flow (BUZZ, the body) runs off the same truth as everything else.
    (async () => {
      try {
        await fetch(`${API_BASE}/api/quests/come_up_the_drop/progress`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
          body: JSON.stringify({ avatarId: myAvatarId, objectiveType: 'goto', targetRef: 'street_studio_exit' }),
        });
        await fetch(`${API_BASE}/api/quests/come_up_the_drop/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
          body: JSON.stringify({ avatarId: myAvatarId }),
        });
      } catch { /* the premiere still plays — quests catch up on next sync */ }
    })();

    // The card holds long enough to be believed, then the street is just a
    // street with your song on it. The premiere itself keeps playing.
    const t = setTimeout(() => {
      setCard(null);
      useWorldStore.getState().setPremiere(null);
    }, 14000);
    return () => clearTimeout(t);
  }, [premiere, interior, myAvatarId]);

  if (!card) return null;

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        <span style={dot} />
        <span style={{ font: '800 11px system-ui', letterSpacing: 2, color: '#ff8a9c' }}>ON AIR · FIRST PLAY</span>
      </div>
      <div style={{ font: '800 20px system-ui', margin: '6px 0 2px' }}>“{card.title}”</div>
      <div style={{ font: '600 11.5px system-ui', opacity: 0.65 }}>{card.station} · the city is listening</div>
    </div>
  );
}

const wrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  top: 'max(56px, calc(env(safe-area-inset-top) + 42px))', zIndex: 72,
  padding: '14px 26px', borderRadius: 18, textAlign: 'center', pointerEvents: 'none',
  color: '#ffe9c4', border: '1px solid rgba(255,138,156,0.5)',
  background: 'rgba(14,9,6,0.92)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const dot = {
  width: 8, height: 8, borderRadius: '50%', background: '#ff5e78',
  boxShadow: '0 0 10px #ff5e78',
};
